import { useCallback, useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { Flex } from 'antd';

import { getEntityEndpoint } from '@/shared/api/openmetadata';
import { tagKeys } from '@/shared/lib/query/queryKeys';
import type { DomainTreeItem } from '@/shared/types/domain';
import type { UpdateOwnerItem } from '@/shared/types/owner';
import { OwnerCard } from '@/shared/ui/OwnerCard';

import { useDisplaySettingsStore } from '@/entities/display-settings';
import { DomainTree } from '@/entities/domain';
import { useOwnerEdit } from '@/entities/owner';
import { type TagDetail, useTagDetail } from '@/entities/tags';

import { useTagDetailDomainEdit } from '@/features/tags/model';

type TagDetailSidebarProps = {
  selectedTagFqn?: string;
};

export const TagDetailSidebar = ({ selectedTagFqn }: TagDetailSidebarProps) => {
  const queryClient = useQueryClient();
  const { data } = useTagDetail({ tagName: selectedTagFqn ?? '' });

  const showOwners = useDisplaySettingsStore((state) => state.showOwners);
  const { domains, id, owners } = data;

  const domainEdit = useTagDetailDomainEdit({
    tagName: selectedTagFqn || '',
  });

  const domainTreeItems: DomainTreeItem[] = useMemo(() => {
    if (!domains) return [];
    return domains.map((domain) => {
      const { id, type, name, fullyQualifiedName, description, displayName, href } = domain;
      return {
        id,
        type,
        name,
        fullyQualifiedName,
        description,
        displayName,
        href,
      };
    });
  }, [domains]);

  const isDisabledDomainTree = useMemo(() => {
    return !selectedTagFqn;
  }, [selectedTagFqn]);

  const queryKey = useMemo(() => (selectedTagFqn ? tagKeys.tagDetail(selectedTagFqn) : []), [selectedTagFqn]);

  const getOwnersFromData = useCallback((data: TagDetail | undefined): UpdateOwnerItem[] => {
    if (!data) return [];
    return data.owners.map((owner) => ({
      id: owner.id,
      type: owner.type,
      name: owner.name,
      displayName: owner.displayName,
      fullyQualifiedName: owner.fullyQualifiedName,
    }));
  }, []);

  const invalidateQuery = useCallback(() => {
    if (selectedTagFqn) {
      queryClient.invalidateQueries({ queryKey: tagKeys.tagDetail(selectedTagFqn) });
    }
  }, [queryClient, selectedTagFqn]);

  const ownerEdit = useOwnerEdit<TagDetail>({
    resourceId: id,
    apiPath: getEntityEndpoint('tag'),
    queryKey,
    getOwnersFromData,
    invalidateQuery,
  });

  const isOwnerEditDisabled = useMemo(() => {
    return !selectedTagFqn || !id;
  }, [selectedTagFqn, id]);

  return (
    <Flex vertical gap={16}>
      <DomainTree
        title="도메인"
        domainData={domainTreeItems}
        onSave={domainEdit.handleSave}
        disabled={isDisabledDomainTree}
        isUpdating={domainEdit.isUpdating}
      />
      {showOwners && (
        <OwnerCard title="소유자" owners={owners ?? []} ownerEdit={ownerEdit} disabled={isOwnerEditDisabled} />
      )}
    </Flex>
  );
};
